import { useHealthContext } from '../context/HealthContext';
import { useLanguage } from '../hooks/useLanguage';
import { useBrand } from '../hooks/useBrand';
import { useContent } from '../hooks/useContent';

// 🏛️ Atomic UI
import { AppFooter } from '../components/snippets/app-footer.snippet.jsx';
import { SystemStatus } from '../components/blocks/system-status.block.jsx';
import { OPEN_COOKIE_SETTINGS_EVENT } from '../components/blocks/cookie-banner.block.jsx';

// The one way back into the cookie banner once it has been dismissed
// (RF-002). Not a route — there is no settings page, only the banner itself,
// reopened in place wherever the footer is.
const openCookieSettings = () => window.dispatchEvent(new CustomEvent(OPEN_COOKIE_SETTINGS_EVENT));
const COOKIE_SETTINGS_LINK = { label: 'Cookie settings', onClick: openCookieSettings };

// Status wording is editable in Dictionary → Site content ("Header & footer").
const CHROME_DEFAULTS = {
  FOOTER: {
    STATUS_OPERATIONAL: 'Systems Operational',
    STATUS_DEGRADED: 'Degraded Performance',
    STATUS_MAINTENANCE: 'Maintenance Mode',
    STATUS_UNKNOWN: 'Service Status Unavailable'
  }
};

// Legal links live at real routes, so they're safe as the default (dictionary
// can override via footer.legalLinks).
const DEFAULT_LEGAL = [
  { to: '/legal/privacy', label: 'Privacy' },
  { to: '/legal/terms', label: 'Terms' },
  { to: '/legal/cookies', label: 'Cookies' }
];

// Marketing footer columns.
//
// WHY THIS GREW FROM SIX LINKS TO THE WHOLE SITE
// ----------------------------------------------
// The note that used to sit here said "add columns/links via Dictionary as new
// public pages land (Careers, Blog, …)". Twenty-nine public pages landed and
// nobody did, so the footer linked six of them. The other twenty-three — every
// /solutions page, the whole reviewer track, the partner programme, trust,
// careers, both contact routes — were reachable only by typing the URL.
//
// That is not a small omission on a marketing site. It is the difference
// between a page existing and a page being read, and it applies to crawlers as
// much as to people: an orphan page with no inbound link is one a search engine
// has no reason to keep.
//
// Every target below is a published page or a declared route. navLinks.test.js
// fails on a dead one, and `unreachablePages` in footerCoverage.test.js fails
// when a published page has no way in at all — which is what let this happen.
//
// Still overridable from Dictionary → Site content (footer.columns); this is
// the default, not the law.
const DEFAULT_COLUMNS = [
  { title: 'Product', links: [
    { to: '/product/how-it-works', label: 'How it works' },
    { to: '/product/features', label: "What's in a Blueprint" },
    { to: '/pricing', label: 'Pricing' },
    { to: '/integrations', label: 'Integrations' },
    { to: '/pricing/mox', label: 'Membership & MOX' },
    { to: '/resources/guides', label: 'Guides' }
  ] },
  { title: 'Solutions', links: [
    { to: '/solutions/individuals', label: 'Founders' },
    { to: '/solutions/teams', label: 'Teams & startups' },
    { to: '/solutions/agencies', label: 'Agencies' },
    { to: '/solutions/enterprise', label: 'Enterprise' },
    { to: '/solutions/education', label: 'Education' },
    { to: '/solutions/public-sector', label: 'Public sector' }
  ] },
  { title: 'Reviewers', links: [
    // ⚠️ `/become-a-reviewer`, not `/reviewer`.
    //
    // This pointed at the authenticated dashboard page, so the one link
    // on the public site inviting people to review sent a logged-out
    // visitor — which is everybody who has not applied yet — somewhere
    // they could not read.
    { to: '/become-a-reviewer', label: 'Become a reviewer' },
    { to: '/reviewer/process', label: 'How review works' },
    { to: '/reviewer/levels', label: 'The Ladder' },
    { to: '/reviewer/payouts', label: 'Payouts' },
    { to: '/reviewer/conduct', label: 'Code of conduct' },
    { to: '/reviewer-pricing', label: 'Reviewer Membership' },
    { to: '/reviewers', label: 'Directory' }
  ] },
  { title: 'Company', links: [
    { to: '/about', label: 'About' },
    { to: '/careers', label: 'Careers' },
    { to: '/partners', label: 'Partners' },
    { to: '/press', label: 'Press' },
    { to: '/contact/sales', label: 'Talk to sales' },
    { to: '/contact/support', label: 'Get help' },
    { to: '/help', label: 'Help centre' }
  ] },
  { title: 'Trust', links: [
    { to: '/trust', label: 'Trust & security' },
    { to: '/legal/dpa', label: 'Data processing' },
    { to: '/legal/sub-processors', label: 'Sub-processors' },
    { to: '/legal/accessibility', label: 'Accessibility' },
    { to: '/legal/acceptable-use', label: 'Acceptable use' },
    { to: '/legal/modern-slavery', label: 'Modern slavery' }
  ] }
];

// Normalise a dictionary link ({ href } or { to }) so root-relative targets
// stay client-side (<Link>) and external ones leave the SPA (<a>).
const normLink = (item) => (
  String(item.href || item.to || '').startsWith('/')
    ? { to: item.href || item.to, label: item.label }
    : { href: item.href || item.to, label: item.label }
);
const normColumns = (cols) => cols
  .filter((c) => c?.title && Array.isArray(c.links))
  .map((c) => ({ title: c.title, links: c.links.filter((l) => l?.label && (l.href || l.to)).map(normLink) }));

/**
 * Footer wrapper — feeds the AppFooter snippet from brand + dictionary + health.
 *
 * @param {'app'|'marketing'|'slim'} variant  page-context chrome (see
 *   docs/HEADER_FOOTER_PLAN.md). Defaults to the lean 'app' row.
 */
export default function Footer({ variant = 'app' }) {
  const health = useHealthContext();
  const brand = useBrand();
  const footer = useLanguage('footer');
  const { t } = useContent('chrome', CHROME_DEFAULTS);
  const year = new Date().getFullYear();
  const status = health?.infrastructure?.status || 'UNKNOWN';
  const isOnline = !['DOWN', 'ERROR', 'UNKNOWN'].includes(status);
  const statusLabel = (() => {
    if (status === 'MAINTENANCE') return t('FOOTER.STATUS_MAINTENANCE');
    if (status === 'DEGRADED' || status === 'WARN') return t('FOOTER.STATUS_DEGRADED');
    if (isOnline) return t('FOOTER.STATUS_OPERATIONAL');
    return t('FOOTER.STATUS_UNKNOWN');
  })();

  // Flat link row (the 'app' / 'slim' variants) — from the dictionary, deduped.
  // Cookie settings is appended last, after the dictionary content: it is an
  // in-page action, not a route, so it is never part of what Dictionary
  // overrides, only ever added to it (RF-002).
  const footerLinks = Array.isArray(footer?.links) ? footer.links : [];
  const links = [...footerLinks, ...brand.footerLinks]
    .filter((item) => item?.href && item?.label)
    .map(normLink)
    .filter((item, index, all) => (
      all.findIndex((other) => (other.to || other.href) === (item.to || item.href)) === index
    ))
    .concat(COOKIE_SETTINGS_LINK);

  // Marketing-only data — all editable in Dictionary → Brand & meta (via
  // useBrand). Falls back to sensible, existing-route defaults until configured.
  const columns = brand.footerColumns?.length
    ? brand.footerColumns.map((c) => ({ title: c.title, links: c.links.map(normLink) }))
    : DEFAULT_COLUMNS;
  const legalLinks = (brand.footerLegalLinks?.length
    ? brand.footerLegalLinks.filter((l) => l?.label && (l.href || l.to)).map(normLink)
    : DEFAULT_LEGAL
  ).concat(COOKIE_SETTINGS_LINK);
  const tagline = brand.footerTagline || 'Turn your idea into a build-ready plan.';
  const cta = brand.footerCta ? normLink(brand.footerCta) : { to: '/#start', label: 'Start free' };
  const banner = brand.footerBanner && (brand.footerBanner.image || brand.footerBanner.text)
    ? { image: brand.footerBanner.image || '', text: brand.footerBanner.text || '', href: brand.footerBanner.href || '' }
    : null;
  const companyInfo = brand.footerCompany || '';
  const legalLine = brand.footerLegalLine || '';

  // The stacked lockup is the right shape for a footer mark; useBrand falls
  // back to the primary logo when no vertical variant is configured.
  const brandName = brand.name;
  const brandLogo = brand.verticalLogo;

  return (
    <AppFooter
      variant={variant}
      brandName={brandName}
      brandLogo={brandLogo}
      copyright={`© ${year} ${brand.copyright}`}
      docLink={brand.documentationLink}
      docText={brand.documentationText}
      links={links}
      socialLinks={brand.socialLinks}
      columns={columns}
      legalLinks={legalLinks}
      tagline={tagline}
      cta={cta}
      banner={banner}
      companyInfo={companyInfo}
      legalLine={legalLine}
      status={
        <SystemStatus
          isOnline={isOnline}
          label={statusLabel}
          version={brand.version}
        />
      }
    />
  );
}
